import styled from 'styled-components'
import PropTypes from 'prop-types'

export const HeroBannerContainer = styled.div`
  width: 100%;
  min-height: 250px;
  background-image: ${props => (props.desktopImage && !props.isLoading) ? `url(${props.desktopImage})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: ${props => props.isCarouselSlide ? '0' : '16px'};
  padding: 32px;
  margin-bottom: ${props => props.isCarouselSlide ? '0' : '16px'};
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease, opacity 0.3s ease;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  opacity: ${props => props.isLoading ? 0.7 : 1};
  box-sizing: border-box;

  /* Overlay de leitura: melhora o contraste do texto sobre qualquer recorte da imagem */
  ${props => (!props.isLoading && (props.desktopImage || props.mobileImage)) && `
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.25) 55%, rgba(0, 0, 0, 0) 100%);
      z-index: 0;
      pointer-events: none;
    }
  `}

  /* Animação de loading */
  ${props => props.isLoading && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.2),
        transparent
      );
      animation: shimmer 1.5s infinite;
    }

    @keyframes shimmer {
      0% {
        left: -100%;
      }
      100% {
        left: 100%;
      }
    }
  `}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: 2px solid #667eea;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    min-height: 280px;
    padding: 24px;
    margin-bottom: ${props => props.isCarouselSlide ? '0' : '12px'};
    border-radius: ${props => props.isCarouselSlide ? '0' : '12px'};
    background-image: ${props => {
      if (props.isLoading) return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      if (props.mobileImage) return `url(${props.mobileImage})`;
      if (props.desktopImage) return `url(${props.desktopImage})`;
      return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }};
  }
`

export const HeroBannerContent = styled.div`
  max-width: 600px;
  min-width: 0;
  z-index: 1;
  color: white;

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
  }
`

export const HeroBannerTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 12px 0;
  color: white;
  overflow-wrap: break-word;
  word-break: break-word;
  /* Mostra o texto completo, quebrando linha; se exceder, corta com reticências */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 24px;
    margin: 0 0 8px 0;
    -webkit-line-clamp: 2;
  }
`

export const HeroBannerSubtitle = styled.h2`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0 0 24px 0;
  color: rgba(255, 255, 255, 0.95);
  overflow-wrap: break-word;
  word-break: break-word;
  /* Mostra o texto completo, quebrando linha; se exceder, corta com reticências */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 14px;
    margin: 0 0 20px 0;
    -webkit-line-clamp: 2;
  }
`

export const HeroBannerButton = styled.button`
  background: transparent;
  border: 2px solid white;
  color: white;
  padding: 12px 32px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  .arrow {
    transition: transform 0.3s ease;
  }

  &:hover {
    background: white;
    color: #667eea;
    
    .arrow {
      transform: translateX(4px);
    }
  }

  @media (max-width: 768px) {
    padding: 10px 24px;
    font-size: 14px;
    width: 100%;
    justify-content: center;
  }
`

export const CarouselWrapper = styled.div`
  /* width explícito: o container da página é flex com align-items: flex-start,
     onde um bloco sem largura declarada encolhe até o conteúdo */
  width: 100%;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  margin-bottom: 16px;
  /* Permite rolagem vertical da página, mas captura o gesto horizontal para o swipe */
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
  cursor: ${props => props.isDragging ? 'grabbing' : 'grab'};

  @media (max-width: 768px) {
    border-radius: 12px;
    margin-bottom: 12px;
  }
`

export const CarouselTrack = styled.div`
  display: flex;
  width: 100%;
  transition: ${props => props.isDragging ? 'none' : 'transform 0.35s ease'};
  will-change: transform;
`

export const CarouselSlide = styled.div`
  /* flex-basis definido + shrink/grow 0 travam o slide em exatamente 100% da viewport.
     min-width: 0 impede que o conteúdo (texto longo) force o slide a crescer além disso,
     garantindo que o texto quebre linha em vez de transbordar horizontalmente. */
  flex: 0 0 100%;
  min-width: 0;
  max-width: 100%;
`

export const DotsContainer = styled.div`
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  z-index: 2;
  pointer-events: none; /* Deixa cliques passarem para o banner abaixo; os Dots restabelecem com pointer-events: all */

  @media (max-width: 768px) {
    bottom: 12px;
  }
`

export const Dot = styled.button`
  width: ${props => props.isActive ? '24px' : '8px'};
  height: 8px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.25);
  background: ${props => props.isActive ? '#ffffff' : 'rgba(255, 255, 255, 0.75)'};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.55);
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;
  pointer-events: all;

  &:hover {
    background: #ffffff;
  }

  &:focus {
    outline: 2px solid rgba(255, 255, 255, 0.9);
    outline-offset: 2px;
  }
`

// PropTypes para validação de tipos
HeroBannerContainer.propTypes = {
  desktopImage: PropTypes.string,
  mobileImage: PropTypes.string,
  isLoading: PropTypes.bool,
  isCarouselSlide: PropTypes.bool,
}

Dot.propTypes = {
  isActive: PropTypes.bool,
}

CarouselWrapper.propTypes = {
  isDragging: PropTypes.bool,
}

CarouselTrack.propTypes = {
  isDragging: PropTypes.bool,
}
